import { FC, useEffect, useMemo, useState } from "react";
import { GetRechnungDetailsDto, SendBillEmailDto } from "../../../../api-client";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, CheckCircle2, FileText, Loader2, Mail } from "lucide-react";
import { useSendBillEmail } from "../../../../stores/Transactions/transaction.mutations";
import { formatDateUI } from "../../../../helpers/dateFormat";

interface SendBillEmailDialogProps {
  open: boolean;
  onClose: () => void;
  rechnung: GetRechnungDetailsDto;
}

/**
 * Dialog to email a patient's billing documents (Rechnung and/or Mahnungen).
 *
 * The email subject, body and template are fixed server-side – the user only
 * chooses which documents to attach. By default the Rechnung and the highest
 * available Mahnung are pre-selected. Already-sent documents can be re-sent but
 * are flagged with a "Bereits gesendet am …" badge.
 */
const SendBillEmailDialog: FC<SendBillEmailDialogProps> = ({ open, onClose, rechnung }) => {
  const sendEmail = useSendBillEmail();

  const recipientEmail = rechnung.patientEmail?.trim() ?? "";
  const hasEmail = recipientEmail.length > 0;
  const hasRechnungPdf = Boolean(rechnung.docDto?.id);

  /** Mahnungen that actually have a generated PDF, sorted by level ascending. */
  const reminders = useMemo(
    () =>
      (rechnung.billReminders ?? [])
        .filter((r) => Boolean(r.document?.id))
        .slice()
        .sort((a, b) => a.level - b.level),
    [rechnung.billReminders],
  );

  const highestReminderId = reminders.length ? reminders[reminders.length - 1].id : undefined;

  const [includeRechnung, setIncludeRechnung] = useState(hasRechnungPdf);
  const [selectedReminderIds, setSelectedReminderIds] = useState<Set<string>>(new Set());

  // Reset selection whenever a different Rechnung is opened.
  useEffect(() => {
    if (!open) return;
    setIncludeRechnung(hasRechnungPdf);
    setSelectedReminderIds(new Set(highestReminderId ? [highestReminderId] : []));
  }, [open, rechnung.id, hasRechnungPdf, highestReminderId]);

  const toggleReminder = (id: string) => {
    setSelectedReminderIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const nothingSelected = !includeRechnung && selectedReminderIds.size === 0;
  const canSend = hasEmail && !nothingSelected && !sendEmail.isPending;

  const handleSend = () => {
    if (!canSend) return;
    const dto: SendBillEmailDto = {
      includeRechnung,
      billReminderIds: Array.from(selectedReminderIds),
    };
    sendEmail.mutate(
      {
        rechnungId: rechnung.id,
        patientId: rechnung.patientId,
        rxId: rechnung.rxId,
        dto,
      },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !sendEmail.isPending) onClose();
      }}
    >
      <DialogContent className="w-[460px] max-w-[95vw] gap-0 overflow-hidden bg-slate-50 p-0">
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-slate-200 bg-white px-5 py-4">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-600">
            <Mail className="h-5 w-5" />
          </div>
          <div className="min-w-0 pr-6">
            <DialogTitle className="text-lg font-semibold leading-tight text-slate-900">
              E-Mail senden
            </DialogTitle>
          </div>
        </div>

        <DialogDescription className="sr-only">
          Wählen Sie die Dokumente aus, die per E-Mail an den Patienten gesendet werden sollen.
        </DialogDescription>

        {/* Body */}
        <div className="space-y-4 bg-slate-200 p-4">
          {/* Recipient */}
          <section>
            <p className="mb-2 text-sm font-semibold text-slate-800">Empfänger</p>
            {hasEmail ? (
              <div className="flex items-center gap-2.5 rounded-lg border border-slate-200 bg-white px-3.5 py-3">
                <Mail className="h-5 w-5 flex-shrink-0 text-slate-800" />
                <span className="truncate text-base text-slate-800">{recipientEmail}</span>
              </div>
            ) : (
              <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" />
                <span className="text-base text-slate-700">
                  Für diesen Patienten ist keine E-Mail-Adresse hinterlegt.
                </span>
              </div>
            )}
          </section>

          {/* Document picker */}
          <section>
            <p className="mb-2 text-sm font-semibold text-slate-800">Dokumente</p>
            <div className="space-y-2">
              {hasRechnungPdf && (
                <DocumentRow
                  checked={includeRechnung}
                  onToggle={() => setIncludeRechnung((v) => !v)}
                  label={`Rechnung ${rechnung.rechnungNumber}`}
                  sentAt={rechnung.emailSentAt}
                  disabled={!hasEmail}
                />
              )}

              {reminders.map((reminder) => (
                <DocumentRow
                  key={reminder.id}
                  checked={selectedReminderIds.has(reminder.id)}
                  onToggle={() => toggleReminder(reminder.id)}
                  label={`${reminder.level}. Mahnung`}
                  sentAt={reminder.emailSentAt ?? (reminder.sentByEmail ? "" : undefined)}
                  disabled={!hasEmail}
                />
              ))}

              {!hasRechnungPdf && reminders.length === 0 && (
                <p className="rounded-lg border border-slate-200 bg-white px-3.5 py-3 text-base text-slate-500">
                  Keine versendbaren Dokumente vorhanden.
                </p>
              )}
            </div>
          </section>
        </div>

        {/* Footer */}
        <DialogFooter className="gap-2 border-t border-slate-200 bg-slate-200 px-5 py-3">
          <Button
            variant="ghost"
            size="default"
            onClick={onClose}
            disabled={sendEmail.isPending}
            className="text-base"
          >
            Abbrechen
          </Button>
          <Button
            size="default"
            onClick={handleSend}
            disabled={!canSend}
            className="bg-slate-800 text-base text-white hover:bg-slate-700"
          >
            {sendEmail.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Wird gesendet…
              </>
            ) : (
              <>
                <Mail className="h-4 w-4" />
                Senden
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface DocumentRowProps {
  checked: boolean;
  onToggle: () => void;
  label: string;
  /** Defined (possibly empty string) when the document was already emailed. */
  sentAt?: string;
  disabled?: boolean;
}

const DocumentRow: FC<DocumentRowProps> = ({ checked, onToggle, label, sentAt, disabled }) => {
  const alreadySent = sentAt !== undefined;
  return (
    <label
      className={`flex items-center gap-3 rounded-lg border bg-white px-3.5 py-3 transition-colors ${
        disabled
          ? "cursor-not-allowed border-slate-200 opacity-60"
          : checked
            ? "cursor-pointer border-slate-400 shadow-sm"
            : "cursor-pointer border-slate-200 hover:border-slate-300"
      }`}
    >
      <Checkbox
        checked={checked}
        onCheckedChange={onToggle}
        disabled={disabled}
        className="h-5 w-5"
      />
      <FileText className="h-5 w-5 flex-shrink-0 text-slate-400" />
      <span className="flex-1 truncate text-base font-medium text-slate-800">{label}</span>
      {alreadySent && (
        <Badge
          variant="outline"
          className="flex-shrink-0 gap-1 border-slate-200 bg-slate-50 text-xs font-medium text-slate-500"
        >
          <CheckCircle2 className="h-3.5 w-3.5" />
          {sentAt ? `Gesendet am ${formatDateUI(sentAt)}` : "Bereits gesendet"}
        </Badge>
      )}
    </label>
  );
};

export default SendBillEmailDialog;
